<template>
  <card-component v-if="loading" :loading="loading" size=" " class="resource-search-card loading" />
  <card-component
    v-else
    class="resource-search-card"
    size=" "
    :aria-label="ariaLabelConfig?.cardComponent"
    @on-click="handlePrimaryCardAction"
    @keydown.enter="handlePrimaryCardAction"
  >
    <template #thumbnail>
      <!--  Media img or icon-->
      <img
        v-if="showMediaImage"
        :src="src"
        :alt="title"
        :data-uuid="uuid"
        :data-downloadable="downloadable"
        data-show-credit="false"
        data-show-group-access="false"
        class="thumbnail"
      />
      <div v-else-if="showMediaIcon" class="wrapper">
        <svg-icon :name="getMediaTypeIcon(resourceType)" kind="indicative" />
      </div>

      <!-- Lesson thumbnail-->
      <div v-if="showLessonThumbnail" v-show="imgLoaded" class="thumbnail-placeholder">
        <img :src="thumbnailUrl" class="thumbnail" @load="onImageLoaded" />
      </div>

      <!-- Question thumbnail | New Lesson icon-->
      <component
        :is="questionThumbnail"
        v-else-if="isQuestionResource || showNewLesson"
        class="thumbnail"
      />

      <!-- Website thumbnail -->
      <div v-if="isWebsiteResource" class="wrapper">
        <img v-if="showWebsiteThumbnail" :src="getIconUrlWithDate()" />
        <svg-icon v-else name="Website32" kind="indicative" />
      </div>

      <!-- App thumbnail -->
      <div v-if="isAppResource" class="wrapper">
        <img :src="getIconUrlWithDate()" />
      </div>

      <!-- Rubric thumbnail-->
      <component :is="rubricsThumbnailComponent" v-if="isRubricResource" />

      <!-- no thumbnail  -->
      <!--  for media-->
      <div v-else class="no-thumbnail" />

      <div
        v-if="showGamificationIcon"
        class="game-enabled"
        role="img"
        :aria-label="ariaLabelConfig?.gamification"
      />
    </template>

    <template #header>
      <!-- coloured label above title -->
      <label-component
        :kind="labelKind"
        :class="getCssClasses()"
        :text="getLocationLabel(resourceDomain, type)"
      />
      <h5 v-if="showHeaderTitle" v-safe-html="titleHighlighted || title" class="title" />
    </template>

    <template #metadata>
      <!-- small grey texts below title -->
      <div class="info-wrapper">
        <li>
          <span>{{ getResourceLabel() }}</span>
        </li>
        <li v-if="showResourceTypeLabel">
          <span>{{ getTypeLabel() }}</span>
        </li>
        <li v-if="showPdIcon" role="img" :aria-label="ariaLabelConfig?.pdIcon">
          <svg-icon name="PD16" kind="indicative" />
        </li>
        <li v-if="subjectLevelList.length">
          <span v-for="(item, index) in subjectLevelList" :key="index" class="subject-level">
            <li
              v-if="item.subjectName"
              v-safe-html="`${item.subjectName} - ${item.subjectKey}`"
              class="subject"
            />
            <li
              v-if="item.levelAndStreamName"
              v-safe-html="populateLevelAndStreamLabel(item, index)"
              class="level"
            />
          </span>
        </li>
      </div>
      <div v-if="isLessonResource" class="count-wrapper">
        <li class="view-count number-abbr-ui" role="img" :aria-label="ariaLabelConfig?.viewCount">
          <svg-icon name="View16" kind="action" />
          <span>{{ viewCountLabel }}</span>
        </li>
        <li>
          {{ teacherReviewCountLabel }}
        </li>
        <li>
          {{ votesCountLabel }}
        </li>
      </div>
    </template>

    <template #content>
      <div v-safe-html="content" class="information" />
      <ul v-if="showContentDetails" class="metadata secondary">
        <li>
          <span class="label">{{ t("search_card_created_by_label") }}</span>
          <span v-safe-html="author" class="value" />
        </li>
        <li>
          <span class="label">{{ t("search_card_created_date_label") }}</span>
          <span class="value">{{ formatDate(createdDate) }}</span>
        </li>
        <li>
          <span class="label">{{ t("search_card_last_edited_label") }}</span>
          <span class="value">{{ formatDate(modifiedDate) }}</span>
        </li>
      </ul>
    </template>

    <template #actions>
      <!-- Open in new tab button -->
      <div
        v-if="showOpenInNewTab && (!isMobile || isAppResource || isWebsiteResource)"
        class="icon-button-wrapper"
        :aria-label="ariaLabelConfig?.openInNewTab"
      >
        <cv-button
          class="icon-button btn-open-in-new-tab"
          kind="ghost"
          tabindex="-1"
          @click.stop="handleOpenInNewTab"
        >
          <svg-icon name="ExternalLink24" kind="action" />
        </cv-button>
      </div>

      <!-- Embed button -->
      <div v-if="showEmbed" class="icon-button-wrapper">
        <cv-button class="icon-button" kind="ghost" tabindex="-1" @click.stop="handleEmbed">
          <svg-icon name="Embed24" kind="action" />
        </cv-button>
      </div>

      <!-- Download button -->
      <div v-if="downloadable" class="icon-button-wrapper" :aria-label="ariaLabelConfig?.download">
        <cv-button class="icon-button" kind="ghost" tabindex="-1" @click="handleDownload">
          <svg-icon name="Download24" kind="action" />
        </cv-button>
      </div>

      <!-- Overflow container for buttons -->
      <overflow-menu
        v-if="showOverflowMenu"
        class="menu-actions"
        :flip-menu="true"
        :aria-label="ariaLabelConfig?.overflowMenu"
        tabindex="-1"
      >
        <template #trigger>
          <svg-icon name="Kebab24" kind="action" />
        </template>

        <!-- Copy To -->
        <OverflowMenuItem v-if="showCopyTo" @click="handleCopyTo">
          <svg-icon name="Copy24" kind="action" />
          <span>{{ t("search_result_card_copy_to_menu_label") }}</span>
        </OverflowMenuItem>

        <!-- Show Details -->
        <OverflowMenuItem v-if="isMobile && showOpenInNewTab" @click="handleOpenInNewTab">
          <svg-icon name="ExternalLink24" kind="action" />
          <span>{{ t("search_card_open_module_label") }}</span>
        </OverflowMenuItem>

        <!-- View Reviews -->
        <OverflowMenuItem v-if="showViewReviews" @click="handleViewReviews">
          <svg-icon name="Review24" kind="action" />
          <span>{{ t("search_result_card_view_reviews_menu_label") }}</span>
        </OverflowMenuItem>
      </overflow-menu>
      <!-- Radio button -->
      <!-- Selection: Single (Radio) & Multiple (Checkbox) -->
      <input-radio
        v-if="selectionMode === 'single'"
        :id="uuid"
        kind="grey"
        value="true"
        :selected-value="String(selected)"
        @change="handleSelect"
      />
      <input-checkbox
        v-else-if="selectionMode === 'multiple'"
        :id="uuid"
        value="true"
        :selected-values="[String(selected)]"
        @change="handleSelect"
      />
    </template>
  </card-component>
</template>
<script setup lang="ts">
import ResourceSearchCardUtils from "@utils/resource.search.card.utils.ts";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import CardComponent from "@plugins/card/components/CardComponent.vue";
import LabelComponent from "@plugins/common/components/LabelComponent.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import OverflowMenu from "@plugins/common/components/OverflowMenu.vue";
import OverflowMenuItem from "@plugins/common/components/OverflowMenuItem.vue";
import { isDefined, isEmpty } from "@utils/var.check.ts";
import StringUtils from "@utils/string.utils.ts";
import { FILE_MEDIA_TYPE } from "@plugins/media/media.constant.ts";
import NewLesson from "@images/generic/NewLesson.svg?component";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import DeviceUtils from "@utils/device.utils.ts";
import {
  getLocationLabel,
  getMediaTypeIcon,
  getMediaTypeLabel,
  getQuestionThumbnail,
  getQuestionTypeLabel,
  RESOURCE_SEARCH_TYPE,
} from "@modules/resource-search/resource-search-helper.ts";
import { ResourceSearchCardProps } from "@/common/card-types.ts";
import { SubjectLevel } from "@/common/subject-level-types.ts";
import ResourceCardUtils from "@utils/resource.card.utils.ts";
import RubricsThumbnail from "@images/lesson/Question/Rubrics.svg";
import { QuestionType } from "@/types/question";

// Props
const props = withDefaults(defineProps<ResourceSearchCardProps>(), {
  loading: false,
  content: "",
  resourceType: "",
  thumbnailUrl: "",
  title: "",
  titleHighlighted: "",
  uuid: "",
  src: "",
  gameEnabled: false,
  subjectLevelList: () => [],
  viewCount: 0,
  teacherReviewCount: 0,
  votesCount: 0,
  author: "",
  createdDate: "",
  modifiedDate: "",
  iconUrl: "",
  showOpenInNewTab: false,
  showViewReviews: false,
  showCopyTo: false,
  showPdIcon: false,
  showEmbed: false,
  downloadable: false,
  selectionMode: null,
  selected: false,
  ariaLabelConfig: null,
});

interface Emits {
  (event: "click"): void;

  (event: "copy"): void;

  (event: "embed"): void;

  (event: "download"): void;

  (event: "view-reviews"): void;

  (event: "open-in-new-tab"): void;

  (event: "select", uuid: string): void;
}

const emit = defineEmits<Emits>();

const { t } = useI18n();
const showResourceTypeLabel = ref<boolean>(
  props.type === RESOURCE_SEARCH_TYPE.QUESTION || props.type === RESOURCE_SEARCH_TYPE.MEDIA,
);

const imgLoaded = ref<boolean>(false);

// Computed
const questionThumbnail = computed(() => {
  // new lesson case
  if (isLessonResource.value && showNewLesson.value) {
    return NewLesson;
  }

  return getQuestionThumbnail(props.resourceType as QuestionType);
});

const rubricsThumbnailComponent = computed(() => {
  return RubricsThumbnail;
});

const labelKind = computed(() => {
  return props.type ? ResourceCardUtils.getLabelKind(props.type) : "";
});

const showOverflowMenu = computed<boolean>(() => {
  if (isMobile.value) {
    return (
      (props.showOpenInNewTab && !isAppResource.value && !isWebsiteResource.value) ||
      props.showViewReviews ||
      props.showCopyTo
    );
  } else {
    //tablet and desktop view
    return props.showViewReviews || props.showCopyTo;
  }
});
const showWebsiteThumbnail = computed<boolean>(
  () => isWebsiteResource.value && !isEmpty(props.iconUrl),
);
const showHeaderTitle = computed<boolean>(() => !!props.title);

const showContentDetails = computed<boolean>(
  () => !!props.author || !!props.createdDate || !!props.modifiedDate,
);
const isLessonResource = computed<boolean>(() => props.type === RESOURCE_SEARCH_TYPE.LESSON);
const showGamificationIcon = computed<boolean>(() => isLessonResource.value && props.gameEnabled);
const showLessonThumbnail = computed<boolean>(() => isLessonResource.value && !!props.thumbnailUrl);
const showNewLesson = computed<boolean>(() => isLessonResource.value && !showLessonThumbnail.value);

const isMediaResource = computed<boolean>(() => props.type === RESOURCE_SEARCH_TYPE.MEDIA);
const showMediaImage = computed<boolean>(
  () =>
    isMediaResource.value &&
    (props.resourceType === "VIDEO" || props.resourceType === "IMAGE") &&
    !isEmpty(props.src),
);
const showMediaIcon = computed<boolean>(
  () => isMediaResource.value && FILE_MEDIA_TYPE.includes(props.resourceType),
);
const isQuestionResource = computed<boolean>(() => props.type === RESOURCE_SEARCH_TYPE.QUESTION);
const isWebsiteResource = computed<boolean>(() => props.type === RESOURCE_SEARCH_TYPE.WEBSITE);
const isAppResource = computed<boolean>(() => props.type === RESOURCE_SEARCH_TYPE.APP);
const isRubricResource = computed<boolean>(() => props.type === RESOURCE_SEARCH_TYPE.RUBRIC);
const votesCountLabel = computed<string>(() => {
  return t("search_result_card_view_lesson_vote_count_label", {
    count: props.votesCount,
  });
});

const teacherReviewCountLabel = computed<string>(() => {
  return t("search_result_card_view_teacher_review_count_label", {
    count: props.teacherReviewCount,
  });
});

const viewCountLabel = computed<string>(() => {
  return isDefined(props.viewCount)
    ? StringUtils.formatNumberWithAbbreviation(props.viewCount)
    : props.viewCount;
});

const isMobile = computed<boolean>(() => DeviceUtils.isMobile());

// Methods
const getIconUrlWithDate = (): string => {
  return `${props.iconUrl || ""}?t=${Date.now()}`;
};

const getResourceLabel = () => {
  switch (props.type) {
    case RESOURCE_SEARCH_TYPE.LESSON:
      return t("resource_search_card_resource_type_module_label");
    case RESOURCE_SEARCH_TYPE.QUESTION:
      return t("resource_search_card_resource_type_question_label");
    case RESOURCE_SEARCH_TYPE.MEDIA:
      return t("resource_search_card_resource_type_media_label");
    case RESOURCE_SEARCH_TYPE.WEBSITE:
      return t("resource_search_card_resource_type_website_label");
    case RESOURCE_SEARCH_TYPE.APP:
      return t("resource_search_card_resource_type_app_label");
    case RESOURCE_SEARCH_TYPE.RUBRIC:
      return t("resource_search_card_resource_type_rubric_label");
    default:
      return "";
  }
};

const getTypeLabel = () => {
  if (props.type === RESOURCE_SEARCH_TYPE.MEDIA) {
    return getMediaTypeLabel(props.resourceType);
  } else if (props.type === RESOURCE_SEARCH_TYPE.QUESTION) {
    return getQuestionTypeLabel(props.resourceType);
  }
  return "";
};

const formatDate = (dateString: string) => {
  if (isEmpty(dateString)) return "-";

  // Detect compact format
  const compactDateRegex = /^\d{14}$/;
  if (compactDateRegex.test(dateString.trim())) {
    return DateTimeFormatter.formatDateTimeToDateTimeAmPm(dateString);
  }

  // Assume already formatted, return as-is
  return dateString;
};

const getCssClasses = () => {
  return ResourceSearchCardUtils.getCssClasses(props.resourceDomain);
};

const populateLevelAndStreamLabel = (item: SubjectLevel, index: number) => {
  return `${item.levelAndStreamName}${ResourceSearchCardUtils.getSubjectLevelCommas(index, props.subjectLevelList.length)}`;
};

const onImageLoaded = () => {
  imgLoaded.value = true;
};

const handlePrimaryCardAction = () => {
  emit("click");
};

const handleOpenInNewTab = () => {
  emit("open-in-new-tab");
};

const handleEmbed = () => {
  emit("embed");
};

const handleViewReviews = () => {
  emit("view-reviews");
};

const handleCopyTo = () => {
  emit("copy");
};

const handleDownload = () => {
  emit("download");
};

const handleSelect = () => {
  emit("select", props.uuid);
};
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.card-component.resource-search-card {
  display: grid;
  grid-template: minmax(4.5rem, auto) auto/4.5rem auto;
  width: 100%;
  gap: $spacing-05;
  padding: $spacing-05;
  background-color: $ui-01;

  &::before {
    border-top: none;
  }

  &:hover {
    outline: 1px solid $interactive-01;
  }

  .card-header {
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    padding: 0;
    // override LabelComponent margin
    .label-component {
      margin: 0;
      align-self: flex-start;

      span {
        @include carbon--type-style("card-label-01");
        padding: $spacing-01 $spacing-03;
      }
    }

    .title {
      @include line-clamp(2);
      margin-top: $spacing-03;
    }

    .card-label {
      position: static;
      align-self: flex-start;
    }
  }

  .card-content {
    grid-column: 2/-1;
  }

  .card-thumbnail {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    place-self: center;

    > .wrapper {
      height: 72px;
      width: 72px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid $ui-03;
      border-radius: 10px;
    }

    img {
      height: 4.5rem;
      width: 4.5rem;
      object-fit: cover;
      border-radius: 10px;
    }
  }

  .card-actions {
    top: 0.25rem;
    right: 0.25rem;
    align-items: center;
    gap: $spacing-03;

    .icon-button-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 40px;
      height: 40px;
    }

    .btn-open-in-new-tab {
      width: inherit;
      height: inherit;
      padding: 0;
    }
  }

  &.loading {
    height: 160px;
    min-width: 300px;
    display: block;
    grid-column: 1/-1;
    padding: 0;
    background-color: $ui-background;
  }

  &.lesson-search {
    .subject-level-wrapper {
      .sls-icon {
        &[name="PD16"] {
          height: 13px;
          overflow: visible;
        }
      }
    }
  }

  .match-text {
    color: $searchmatch-01;
    font-weight: 700;
  }

  .metadata {
    max-width: 100%;
    gap: $spacing-05;

    @include media("desktop") {
      flex-flow: row nowrap;
    }

    &:not(.secondary) {
      max-width: 100%;
      /*IOS Safari*/
      max-height: 44px;
      margin-top: $spacing-03;
      flex-wrap: wrap;
      gap: 0;

      @include media("tablet-min") {
        flex-wrap: nowrap;
        gap: $spacing-02;
        max-height: 20px;
      }

      li {
        display: inline;
        gap: $spacing-02;
        word-break: break-all;

        &::after {
          display: inline-block;
          height: 4px;
          width: 4px;
          border-radius: 50%;
          transform: scale(0.5);
        }

        &.view-count {
          line-height: 1rem;

          .sls-icon {
            margin-right: $spacing-01;
            vertical-align: sub;
          }
        }

        // to align bolded text same baseline as normal text
        &:has(.match-text) {
          margin-top: -1px;

          &::after {
            top: 45%;
          }
        }
      }
    }

    &.secondary {
      position: relative;
      gap: $spacing-03;
      padding-top: $spacing-05;
      margin-top: $spacing-05;
      border-top: 1px solid $ui-03;

      li {
        flex-wrap: wrap;
        align-items: baseline;
        gap: $spacing-01;

        .label {
          @include carbon--type-style("label-01");
          max-height: 14px;
        }

        .value {
          @include carbon--type-style("card-label-01");
        }

        &::after {
          display: none;
        }
      }
    }

    .subject-level-wrapper {
      @include line-clamp(1);

      & + .count-wrapper {
        @include media("tablet-min") {
          margin-left: 9px;
        }
      }
    }

    .info-wrapper {
      color: $text-02;
      & ~ .count-wrapper {
        @include media("tablet-min") {
          margin-left: 6px;
          &::before {
            left: -8px;
          }
        }
      }
    }

    .count-wrapper {
      position: relative;
      flex: 1 0 auto;
      margin-top: -3px; //added to adjust gap inbetween metadata in mobile

      > li {
        &.view-count {
          &::after {
            top: 7px;
          }
        }
      }

      @include media("tablet-min") {
        margin-top: 2px;
        &::before {
          content: "";
          position: absolute;
          display: inline-block;
          height: 4px;
          width: 4px;
          top: 37%;
          left: -8px;
          transform: translateY(50%) scale(0.5);
          background-color: $icon-02;
          border-radius: 50%;
        }
      }
      @include media("desktop") {
        margin-top: 0;
        &::before {
          top: 42%;
        }
      }
    }
  }

  .game-enabled {
    position: absolute;
    right: -0.5rem;
    bottom: -0.25rem;
    height: 38px;
    width: 38px;
    background: url("/src/assets/images/game/GameIconEnabled.svg") no-repeat;
  }

  .information {
    @include carbon--type-style("body-short-01");

    span {
      @include carbon--type-style("productive-heading-01");
    }
  }

  .info-wrapper {
    @include line-clamp(1);

    .sls-icon {
      &[name="PD16"] {
        height: 13px;
        overflow: visible;
      }
    }
  }
}
</style>
